/**
 * Similar Listings Component
 * Displays listings similar to the current product based on title, category, and tags
 * Only requires listingId - all other data is fetched server-side
 */

import React, { useState, useEffect, useRef } from 'react';
import ListingSlider from './listing-slider';
import ShowcaseStripSkeleton from './search-listing/ShowcaseStripSkeleton';
import { useProduct } from '../contexts';
import { Product } from '../contexts/ProductProvider';
import { useSimilarListings } from '../hooks/useSimilarListings';

interface SimilarListingsProps {
  title: string;
  listingId: number;
  setProduct?: (product: Product, listGroup?: string, isBack?: boolean) => void;
  tag?: string;
  lazy?: boolean;
  minItems?: number;
}

const SimilarListings: React.FC<SimilarListingsProps> = ({
  title,
  listingId,
  setProduct,
  tag,
  lazy = true,
  minItems = 7,
}) => {
  const { setSimilarListings, similarList, currentListGroup } = useProduct();
  const [isVisible, setIsVisible] = useState(!lazy);
  const containerRef = useRef<HTMLDivElement>(null);

  // ONCE SEEN, ALWAYS SEEN. There used to be an effect here that set
  // `isVisible` back to false whenever `currentListGroup` changed, to re-arm
  // the lazy fetch for a new listing. But `currentListGroup` changes as a
  // side effect of `setProduct`, which the product page calls on its own load
  // — so the row collapsed from its 421px strip to the 96px placeholder and
  // grew again as the observer re-fired, twice, while the page was still
  // settling. Measured on staging: 96 -> 421 -> 96 -> 421 inside 400ms, and
  // everything below it moved four times.
  //
  // Nothing needs re-arming: useSimilarListings refetches on a new
  // `listingId` on its own, and keeps the previous cards on screen until the
  // new ones land, so the row holds its height through a flip.

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    if (!lazy || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Any entry, not the first. A frame that is busy hydrating delivers the
        // row's whole history at once, oldest first — on prod the batch was
        // [out of view, in view], and reading only entries[0] left the row a
        // skeleton until the reader scrolled away and back.
        if (entries.some((entry) => entry.isIntersecting) && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        rootMargin: '200px',
        threshold: 0,
      }
    );

    // The wrapper div below always renders with this ref, and effects run after it mounts.
    const node = containerRef.current as HTMLDivElement;
    observer.observe(node);

    return () => observer.unobserve(node);
  }, [lazy, isVisible]);

  const { listings, isLoading, hasError } = useSimilarListings({
    listingId,
    isVisible,
    similarList,
    currentListGroup,
    setSimilarListings,
  });

  // Don't render if we've fetched and there aren't enough items
  // But always render the container initially so intersection observer can attach
  const shouldHide = isVisible && !isLoading && listings.length < minItems && !hasError;

  if (shouldHide) {
    return null;
  }

  return (
    <div ref={containerRef} className="similar-listings">
      {/* Only show content once visible */}
      {isVisible ? (
        <>
          {/* The same heading row every other strip of listings gets. This was
              a fifth copy of it — `text-3xl font-semibold` inside a
              `.showcase-header` that no stylesheet has ever styled — so
              "Similar Listings" came out at 30px over cards whose own titles
              are 15px, while the seller's row right below it was 18px. */}
          <div className="showcase-heading-row ml-6">
            <h2 className="showcase-heading">{title}</h2>
          </div>

          {/*
            A 192px spinner where a 366px row will be still moved everything
            below it when the cards arrived, which on the product page is the
            second ad frame and the seller's row. The placeholder is the strip,
            at the size the strip will be. The heading above is the REAL one —
            the title is known before the fetch, so there is nothing to guess.
          */}
          {isLoading && listings.length === 0 && <ShowcaseStripSkeleton />}

          {/* Listings slider */}
          {listings.length > 0 && (
            <ListingSlider
              list={listings as unknown as React.ComponentProps<typeof ListingSlider>['list']}
              listName="similarList"
              clickable={true}
              tag={tag}
              noMargin={false}
            />
          )}
        </>
      ) : (
        /* The row's own shape, before the observer has fired. It was a bare
           96px box, so the first thing this row did on every product page was
           grow by 325px and push the seller's row and the footer down with it.
           A placeholder is only worth having at the size of the thing it
           precedes — and this one knows its title already. */
        <>
          <div className="showcase-heading-row ml-6">
            <h2 className="showcase-heading">{title}</h2>
          </div>
          <ShowcaseStripSkeleton />
        </>
      )}
    </div>
  );
};

export default SimilarListings;
